import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { Clock, Handshake, MessagesSquare, Users } from 'lucide-react';
import type { GeneralDashboard } from '@/features/negociacoes';
interface KPISectionProps {
  data: GeneralDashboard['block1'];
}
type IconVariant = 'success' | 'primary' | 'information' | 'warning';
const ICON_VARIANTS: Record<IconVariant, {
  bgClass: string;
  fgColor: string;
}> = {
  primary: {
    bgClass: 'bg-[var(--kpi-icon-primary-bg)]',
    fgColor: 'var(--kpi-icon-primary-fg)'
  },
  success: {
    bgClass: 'bg-[var(--kpi-icon-success-bg)]',
    fgColor: 'var(--kpi-icon-success-fg)'
  },
  warning: {
    bgClass: 'bg-[var(--kpi-icon-warning-bg)]',
    fgColor: 'var(--kpi-icon-warning-fg)'
  },
  information: {
    bgClass: 'bg-[var(--kpi-icon-information-bg)]',
    fgColor: 'var(--kpi-icon-information-fg)'
  }
};
type LucideIcon = typeof Clock;
interface KPIBreakdownRow {
  label: string;
  value: string;
}
interface KPICard {
  label: string;
  value: string;
  sublabel: string;
  variant: IconVariant;
  Icon: LucideIcon;
  description?: string;
  breakdown?: KPIBreakdownRow[];
}
const msToDays = (ms: number): string => (ms / 86_400_000).toLocaleString('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});
const EMPTY_ATTEMPTS: GeneralDashboard['block1']['responseAttempts'] = {
  firstAttempt: 0,
  secondAttempt: 0,
  thirdPlusAttempt: 0
};
const buildAttemptRows = (attempts: GeneralDashboard['block1']['responseAttempts']) => [{
  label: '1ª tentativa',
  value: `${attempts.firstAttempt.toLocaleString('pt-BR')} casos`
}, {
  label: '2ª tentativa',
  value: `${attempts.secondAttempt.toLocaleString('pt-BR')} casos`
}, {
  label: '3ª tentativa ou +',
  value: `${attempts.thirdPlusAttempt.toLocaleString('pt-BR')} casos`
}];
const formatResponseTime = (ms: number): string => {
  if (!ms) return '0s';
  const totalSeconds = Math.floor(ms / 1000);
  const days = totalSeconds / 86400;
  if (days >= 1) return `${days.toFixed(1)} dias`;
  const hours = Math.floor(totalSeconds / 3600);
  if (hours >= 1) {
    const minutes = Math.floor(totalSeconds % 3600 / 60);
    return `${hours}h ${minutes}m`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes >= 1) return `${minutes}m ${totalSeconds % 60}s`;
  return `${totalSeconds}s`;
};
export const KPISection: React.FC<KPISectionProps> = t0 => {
  const $ = _c(6);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data.avgResponseTimeMs || $[1] !== data.avgTimeToAgreementMs || $[2] !== data.responseAttempts || $[3] !== data.responseRate || $[4] !== data.totalNegotiations) {
    const totalDisparadas = data.totalNegotiations.toLocaleString("pt-BR");
    const cards = [{
      label: "Total disparadas",
      value: totalDisparadas,
      sublabel: "negocia\xE7\xF5es iniciadas",
      variant: "primary",
      Icon: Users,
      description: "Cada caso conta uma vez, independente do n\xFAmero de tentativas"
    }, {
      label: "Taxa de resposta",
      value: `${data.responseRate.toFixed(2)}%`,
      sublabel: "do total disparado",
      variant: "success",
      Icon: MessagesSquare,
      description: "Percentual de contatos que responderam"
    }, {
      label: "Tempo m\xE9dio de resposta",
      value: formatResponseTime(data.avgResponseTimeMs),
      sublabel: "at\xE9 a primeira resposta",
      variant: "warning",
      Icon: Clock,
      breakdown: buildAttemptRows(data.responseAttempts ?? EMPTY_ATTEMPTS)
    }, {
      label: "Tempo m\xE9dio at\xE9 acordo",
      value: data.avgTimeToAgreementMs != null ? formatResponseTime(data.avgTimeToAgreementMs) : "\u2014",
      sublabel: "do 1\xBA contato ao acordo",
      variant: "information",
      Icon: Handshake,
      breakdown: [{
        label: "Dias at\xE9 1\xAA resposta",
        value: msToDays(data.avgResponseTimeMs)
      }, {
        label: "Dias at\xE9 acordo",
        value: data.avgTimeToAgreementMs != null ? msToDays(data.avgTimeToAgreementMs) : "\u2014"
      }]
    }];
    t1 = <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">{cards.map(_temp2)}</div>;
    $[0] = data.avgResponseTimeMs;
    $[1] = data.avgTimeToAgreementMs;
    $[2] = data.responseAttempts;
    $[3] = data.responseRate;
    $[4] = data.totalNegotiations;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
function _temp(row) {
  return <div key={row.label} className="flex items-center justify-between gap-2 text-xs"><span className="whitespace-nowrap" style={{
      color: "var(--kpi-description)"
    }}>{row.label}</span><span className="font-semibold whitespace-nowrap" style={{
      color: "var(--kpi-value)"
    }}>{row.value}</span></div>;
}
function _temp2(card) {
  const variant = ICON_VARIANTS[card.variant];
  return <div key={card.label} className="flex h-full flex-col rounded-[16px] border border-(--kpi-border) bg-white p-[18px]" style={{
    boxShadow: "var(--kpi-shadow)"
  }}><div className={`mb-3.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] ${variant.bgClass}`}><card.Icon className="h-[18px] w-[18px]" style={{
        color: variant.fgColor
      }} /></div><p className="mb-1.5 text-sm leading-none font-medium" style={{
      color: "var(--kpi-label)"
    }}>{card.label}</p><p className="mb-1 text-2xl leading-none font-bold" style={{
      color: "var(--kpi-value)"
    }}>{card.value}</p><p className="text-xs font-medium" style={{
      color: variant.fgColor
    }}>{card.sublabel}</p>{card.description && <p className="mt-1.5 text-xs leading-[1.4]" style={{
      color: "var(--kpi-description)"
    }}>{card.description}</p>}{card.breakdown && <div className="mt-2 flex flex-col gap-1 border-t border-neutral-100 pt-2">{card.breakdown.map(_temp)}</div>}</div>;
}